<template>
  <EquipmentBase :section-id="'correlations'">
    <EquipmentHeader>
      <template #title>
        <div class="flex justify-content-between align-items-center flex-wrap">
          Correlations
          <Button
            class="toggle p-button-rounded ml-2"
            :class="{ 'fa-rotate-180': showCorrelations }"
            icon="far fa-angle-down"
            @click="showCorrelations = !showCorrelations"
          />
        </div>
      </template>
    </EquipmentHeader>
    <transition name="vertical-slide">
      <div v-if="showCorrelations">
        <CorrelationValidationDropdown />

        <div class="field-grid">
          <form class="flex flex-column" @submit.prevent>
            <p class="heading-3">Casing Flow</p>
            <div class="grid">
              <div v-if="isEspOnShore" class="col-12 lg:col-6">
                <FormLabel v-slot="{ id }" text="Casing Flow">
                  <Dropdown
                    :id="id"
                    v-model="correlationsForm.data.value.casing_flow"
                    :disabled="isScenarioReadonly || isImportedFromLegacy"
                    :options="correlationsOptions?.casing_flow_options"
                    option-label="comment"
                    option-value="value"
                    class="w-full h-full"
                    append-to="body"
                  />
                </FormLabel>
              </div>
              <div v-if="isEspOnShore" class="col-12 lg:col-6">
                <ConverterInput
                  label="Casing Roughness"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :base-unit="LengthUnits.Inches"
                  :target-unit="
                    getSmallLengthUnit('Casing Roughness.write.Inches')
                  "
                  :decimal-places="5"
                  :model="SmallLengthUnits.options()"
                  mode="write"
                  :model-value="
                    correlationsForm.data.value.casing_roughness_inches
                  "
                  @update:model-value="
                    correlationsForm.update({
                      casing_roughness_inches: $event || 0
                    })
                  "
                />
              </div>
              <div class="col-12 lg:col-6 flex justify-content-between md:p-4">
                <LabelledToggle
                  v-if="
                    correlationsForm.data.value.casing_flow ===
                    Casing_Flow_Options_Enum.BeggsBrill
                  "
                  text="Include Palmer Correction"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :model-value="
                    correlationsForm.data.value.casing_palmer_correction ||
                    false
                  "
                  @update:model-value="
                    correlationsForm.data.value.casing_palmer_correction =
                      $event
                  "
                />
                <LabelledToggle
                  v-else-if="
                    correlationsForm.data.value.casing_flow ===
                      Casing_Flow_Options_Enum.HagedornBrown ||
                    correlationsForm.data.value.casing_flow ===
                      Casing_Flow_Options_Enum.Orkiszewski
                  "
                  :model-value="
                    correlationsForm.data.value.casing_griffith_wallis || false
                  "
                  text="Include Griffith &amp; Wallis Correction"
                  @update:model-value="
                    correlationsForm.data.value.casing_griffith_wallis = $event
                  "
                />
                <LabelledToggle
                  :text="`${
                    correlationsForm.data.value
                      .casing_apply_hazen_williams_correction
                      ? 'Disable Hazen-Williams Correction'
                      : 'Enable Hazen-Williams Correction'
                  }`"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :model-value="
                    correlationsForm.data.value
                      .casing_apply_hazen_williams_correction || false
                  "
                  @update:model-value="
                    correlationsForm.data.value.casing_apply_hazen_williams_correction =
                      $event
                  "
                />
              </div>
              <div class="col-12 lg:col-6">
                <ConverterInput
                  v-if="
                    correlationsForm.data.value
                      .casing_apply_hazen_williams_correction
                  "
                  label="Hazen-Williams Coefficient"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :base-unit="Generic"
                  :decimal-places="2"
                  mode="write"
                  :model-value="
                    correlationsForm.data.value
                      .casing_hazen_williams_coefficient
                  "
                  @update:model-value="
                    correlationsForm.update({
                      casing_hazen_williams_coefficient: $event || 0
                    })
                  "
                />
              </div>
            </div>
            <p class="heading-3">Tubing Flow</p>
            <div class="grid">
              <div v-if="isEspOnShore" class="col-12 lg:col-6">
                <FormLabel v-slot="{ id }" text="Tubing Flow">
                  <Dropdown
                    :id="id"
                    v-model="correlationsForm.data.value.tubing_flow"
                    :disabled="isScenarioReadonly || isImportedFromLegacy"
                    :options="correlationsOptions?.tubing_flow_options"
                    option-label="comment"
                    option-value="value"
                    class="w-full h-full"
                    append-to="body"
                  />
                </FormLabel>
              </div>
              <div v-if="isEspOnShore" class="col-12 lg:col-6">
                <ConverterInput
                  label="Tubing Roughness"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :base-unit="LengthUnits.Inches"
                  :target-unit="
                    getSmallLengthUnit('Tubing Roughness.write.Inches')
                  "
                  :decimal-places="5"
                  :model="SmallLengthUnits.options()"
                  mode="write"
                  :model-value="
                    correlationsForm.data.value.tubing_roughness_inches
                  "
                  @update:model-value="
                    correlationsForm.update({
                      tubing_roughness_inches: $event || 0
                    })
                  "
                />
              </div>
              <div class="col-12 lg:col-6 flex justify-content-between md:p-4">
                <LabelledToggle
                  v-if="
                    correlationsForm.data.value.tubing_flow ===
                    Tubing_Flow_Options_Enum.BeggsBrill
                  "
                  text="Include Palmer Correction"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :model-value="
                    correlationsForm.data.value.tubing_palmer_correction ||
                    false
                  "
                  @update:model-value="
                    correlationsForm.data.value.tubing_palmer_correction =
                      $event
                  "
                />

                <LabelledToggle
                  v-else-if="
                    correlationsForm.data.value.tubing_flow ===
                      Tubing_Flow_Options_Enum.HagedornBrown ||
                    correlationsForm.data.value.tubing_flow ===
                      Tubing_Flow_Options_Enum.Orkiszewski
                  "
                  :model-value="
                    correlationsForm.data.value.tubing_griffith_wallis || false
                  "
                  text="Include Griffith &amp; Wallis Correction"
                  @update:model-value="
                    correlationsForm.data.value.tubing_griffith_wallis = $event
                  "
                />
                <LabelledToggle
                  :text="`${
                    correlationsForm.data.value
                      .tubing_apply_hazen_williams_correction
                      ? 'Disable Hazen-Williams Correction'
                      : 'Enable Hazen-Williams Correction'
                  }`"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :model-value="
                    correlationsForm.data.value
                      .tubing_apply_hazen_williams_correction || false
                  "
                  @update:model-value="
                    correlationsForm.data.value.tubing_apply_hazen_williams_correction =
                      $event
                  "
                />
              </div>
              <div class="col-12 lg:col-6">
                <ConverterInput
                  v-if="
                    correlationsForm.data.value
                      .tubing_apply_hazen_williams_correction
                  "
                  label="Hazen-Williams Coefficient"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :base-unit="Generic"
                  :decimal-places="2"
                  mode="write"
                  :model-value="
                    correlationsForm.data.value
                      .tubing_hazen_williams_coefficient
                  "
                  @update:model-value="
                    correlationsForm.update({
                      tubing_hazen_williams_coefficient: $event || 0
                    })
                  "
                />
              </div>
            </div>
            <p class="mt-4 heading-3">PVT</p>

            <CustomFluidProperties />

            <div class="grid">
              <FormLabel
                v-slot="props"
                text="Bubble Point [Pb]"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.bubble_point"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.bubble_point_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Oil Density"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.oil_density"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.oil_density_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Oil Volume Factor [Bo] Saturated"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="
                    correlationsForm.data.value.oil_volume_factor_saturated
                  "
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.oil_volume_factor_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Oil Volume Factor [Bo] Undersaturated"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="
                    correlationsForm.data.value.oil_volume_factor_undersaturated
                  "
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.oil_volume_factor_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Solution Gas [Rso]"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.solution_gor"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.solution_gas_oil_ratio_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Gas Compressibility [Z]"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.gas_compressibility"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.gas_compressibility_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Water Density"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.water_density"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.water_density_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Water Volume Factor [Bw]"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.water_volume_factor"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="formattedWaterVolumeFactorOptions"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Water Solution Gas [Rsw]"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.water_solution_gas"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.water_solution_gas_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                />
              </FormLabel>
            </div>

            <p class="heading-3 mt-4">Viscosity</p>
            <div class="grid">
              <FormLabel v-slot="props" text="Dead Oil" class="col-12 lg:col-6">
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.dead_oil"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="deadOilOptions"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Water Viscosity"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.water_viscosity"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.water_viscosity_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Saturated Oil"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.saturated_oil"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="
                    (correlationsOptions?.saturated_oil_options || []).filter(
                      o => o.value !== 'glaso_north_sea'
                    )
                  "
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <ConverterInput
                label="Water Salinity"
                class="hidden lg:block col-6"
                :disabled="isScenarioReadonly || isImportedFromLegacy"
                :base-unit="ConcentrationUnits.PartsPerMillion"
                :target-unit="ConcentrationUnits.PartsPerMillion"
                :decimal-places="5"
                :model="ConcentrationUnits.options()"
                mode="write"
                :model-value="correlationsForm.data.value.salinity_ppm"
                @update:model-value="
                  correlationsForm.update({ salinity_ppm: $event || 0 })
                "
              />
              <FormLabel
                v-slot="props"
                text="Under Saturated Oil"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.under_saturated_oil"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="
                    (
                      correlationsOptions?.under_saturated_oil_options || []
                    ).filter(o => o.value !== 'glaso_north_sea')
                  "
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
              <FormLabel
                v-slot="props"
                text="Gas Viscosity"
                class="col-12 lg:col-6"
              >
                <Dropdown
                  :id="props.id"
                  v-model="correlationsForm.data.value.gas_viscosity"
                  :disabled="isScenarioReadonly || isImportedFromLegacy"
                  :options="correlationsOptions?.gas_viscosity_options"
                  option-label="comment"
                  option-value="value"
                  class="w-full"
                  append-to="body"
                />
              </FormLabel>
            </div>
            <CustomViscosityProperties />
            <section v-if="isEspOnShore">
              <p class="heading-3 mt-4">Natural Gas Separation</p>
              <div class="grid">
                <FormLabel
                  v-slot="props"
                  text="Natural Gas Separation"
                  class="col-12 lg:col-6"
                >
                  <Dropdown
                    :id="props.id"
                    v-model="correlationsForm.data.value.natural_gas_separation"
                    :disabled="isScenarioReadonly || isImportedFromLegacy"
                    :options="
                      correlationsOptions?.natural_gas_separation_options
                    "
                    option-label="comment"
                    option-value="value"
                    class="w-full"
                    append-to="body"
                  />
                </FormLabel>
              </div>
            </section>

            <p v-if="!user.is_customer" class="heading-3 mt-4">Other Options</p>
            <ConverterInput
              v-if="!user.is_customer"
              class="col-12 lg:col-6"
              label="TDH Tuning Factor"
              :model-value="
                correlationsForm.data.value.tdh_tuning_factor_ratio || 1
              "
              :base-unit="GenericUnits.Generic"
              :decimal-places="3"
              :min="0.5"
              :max="1.5"
              mode="write"
              :model="GenericUnits.options()"
              @update:model-value="
                correlationsForm.update({
                  tdh_tuning_factor_ratio: Number($event?.toFixed(3) || 1)
                })
              "
            />
          </form>
        </div>
      </div>
    </transition>
  </EquipmentBase>
</template>

<script setup lang="ts">
import {
  correlationsFormKey,
  scenarioKey,
  sizingFormKey,
  sizingKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import {
  Dead_Oil_Options_Enum,
  Sizing_Pumping_Type_Enum,
  Tubing_Flow_Options_Enum,
  Casing_Flow_Options_Enum,
  useCorrelationsOptionsQuery,
  Water_Volume_Factor_Options_Enum
} from "gql/api";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import FormLabel from "@/components/FormLabel.vue";
import { computed, ref } from "vue";
import ConverterInput from "@/components/ConverterInput.vue";
import { Generic, GenericUnits } from "@/libs/units/generic";
import { LengthUnits, SmallLengthUnits } from "@/libs/units/length";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { ConcentrationUnits } from "@/libs/units/concentration";
import { injectStrict } from "@/utils/utils";
import LabelledToggle from "@/components/LabelledToggle.vue";
import CorrelationValidationDropdown from "@/components/scenarios/CorrelationValidationDropdown.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import CustomViscosityProperties from "@/views/scenarios/wellInfo/CustomViscosityProperties.vue";
import CustomFluidProperties from "@/views/scenarios/wellInfo/CustomFluidProperties.vue";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const user = useUserInfo();

const showCorrelations = ref(false);
const correlationsForm = injectStrict(correlationsFormKey);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);

const { data: correlationsOptions } = useCorrelationsOptionsQuery();

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const sizingForm = injectStrict(sizingFormKey, null);

const { getSmallLengthUnit } = useScenarioUnits(scenario, sizing);

const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});
// Filter out Jones from options available. This is left in for any
// older scenarios so the calcs still run with Jones but they can't
// choose this option moving forward.
const formattedWaterVolumeFactorOptions = computed(() => {
  return (correlationsOptions.value?.water_volume_factor_options || []).filter(
    option => option.value !== Water_Volume_Factor_Options_Enum.Jones
  );
});

const deadOilOptions = computed(() => {
  if (wellPropertiesForm.data.value.custom_viscosities_properties) {
    const customViscositiesProperties =
      wellPropertiesForm.data.value.custom_viscosities_properties;
    const hasCustomDeadOilValues = customViscositiesProperties?.some(
      (entry: LagrangeInterpolationMapWithGroupings) =>
        entry.deadOilViscosityCP != null
    );
    // Only show the custom option if there are custom dead oil values present
    if (hasCustomDeadOilValues) {
      return correlationsOptions?.value?.dead_oil_options.filter(
        option => option.value === Dead_Oil_Options_Enum.Custom
      );
    }
  }
  // Otherwise show all options except custom
  return correlationsOptions?.value?.dead_oil_options.filter(
    option => option.value !== Dead_Oil_Options_Enum.Custom
  );
});
</script>

<style lang="scss">
.toggle {
  transition: all 100ms;
}
.viscosity-tests {
  display: flex;
  background-color: red;
}
</style>
